import { hydrateRoot } from "react-dom/client";
import PropTypes from "prop-types";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { loadableReady } from "@loadable/component";
import { Provider } from "react-redux";
import { configureStore } from "../app/configureStore";
import ResponsiveStateProvider from "app/pages/.shared/responsive/ResponsiveStateProvider.jsx";
import { LoadingBarProvider } from "app/features/loadingBar";
import { IntlProvider } from "react-intl";
import env from "app/utils/env";
import PerfectAppContainer from "app/PerfectAppContainer";
import { fetchMessages } from "src/intl/intlUtils";
import ScrollToTop from "app/pages/.shared/Scroll/ScrollToTop";
import AnalyticsContext from "app/utils/analytics/AnalyticsContext";
import RumErrorBoundary from "app/datadog/RumErrorBoundary";
import { RumContextProvider } from "app/datadog/RumContextProvider";
import { BRANDS } from "app/constants";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";

//scoped references
const queryClient = new QueryClient({
	defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const initialState = window.__INITIAL_STATE__ || {};
const { shop, brand, envVars, abTests = {} } = initialState;
const store = configureStore(initialState, { persist: true });
const cspNonce = window.__CSP_NONCE__ || "";

const cache = createCache({
	key: "pfs-emotion-key",
	nonce: cspNonce,
	prepend: true,
});

const AppWithErrorBoundary = ({ initialFlagsData }) => {
	const { pathname } = useLocation();
	return (
		<RumErrorBoundary locationKey={pathname}>
			<ScrollToTop />
			<PerfectAppContainer
				initialFlagsData={initialFlagsData} // avoid flickering due to default value of useFsFlag
			/>
		</RumErrorBoundary>
	);
};

AppWithErrorBoundary.propTypes = {
	initialFlagsData: PropTypes.object,
};

if (cspNonce) {
	window.__webpack_nonce__ = cspNonce;
}

let intl;

const apiURL = env("CONTENT_API_URL") || env("BASE_URL");

fetchMessages(apiURL, brand.code, shop).then(messages => {
	// Note : Seulement cote front (voir i18nMiddleware pour la generation serveur)

	let locale = shop;

	if (shop.includes("en") && shop !== "en-US") {
		locale = "en-GB"; // on veut forcer le format en peu importe la country sauf US. ex en-SA on veut afficher june 2025 et non pas Ramadan 1446 AH
	} else if (shop.includes("ar")) {
		locale = "ar"; // astuce : utiliser "ar" et non par "ar-SA" pour avoir les chiffres en 1, 2, 3 ....
	}

	intl = {
		messages,
		locale,
	};
	renderApp();
});

const renderApp = () => {
	const injectAnalyticScripts = () => {
		let gtmId = envVars.GTM_ID;

		if (gtmId) {
			let GTM_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'//www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));
f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer', '${gtmId}');`;

			if (brand?.code === BRANDS.TO) {
				GTM_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://sst.holidays.transavia.fr/p5cloi3t9cro30z.js?aw='+i.replace(/^GTM-/, '')+dl;var n=d.querySelector('[nonce]');n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','${gtmId}');`;
			} else if (brand?.code === BRANDS.AF) {
				GTM_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://sst.holidays.airfrance.fr/h2g46noqxh0zor3.js?aw='+i.replace(/^GTM-/, '')+dl;var n=d.querySelector('[nonce]');n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','${gtmId}');`;
			} else if (brand?.code === BRANDS.TK) {
				GTM_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://sst.holidays.turkishairlines.com/ubffqsrun4hgvz8.js?aw='+i.replace(/^GTM-/, '')+dl;
var n=d.querySelector('[nonce]');n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));
f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
			}

			const script = document.createElement("script");
			if (cspNonce) {
				script.setAttribute("nonce", cspNonce);
			}
			script.text = GTM_SCRIPT;
			const head = document.getElementsByTagName("head")[0];
			head.appendChild(script);
		}
	};

	const checkEnvironnementVariables = () => {
		const { BASE_URL, QUOTE_API_URL } = envVars;

		if (!BASE_URL) {
			throw new Error("Missing environment variable : BASE_URL");
		}

		if (!QUOTE_API_URL) {
			throw new Error("Missing environment variable : QUOTE_API_URL");
		}
	};

	checkEnvironnementVariables();

	const initialFlagsData = abTests?.initialFlagsData;

	const app = (
		<HelmetProvider>
			<CacheProvider value={cache}>
				<QueryClientProvider client={queryClient}>
					<Provider store={store}>
						{/* Pas de PersistGate ici : il rendrait `null` tant que redux-persist n'a pas
						    fini de réhydrater (asynchrone), produisant un #perfectstay vide au premier
						    rendu alors que le serveur a envoyé le HTML complet. hydrateRoot (React 18,
						    strict) ne peut pas réconcilier vide-contre-plein → SSR + client empilés.
						    Le store est déjà seedé avec window.__INITIAL_STATE__ (= état serveur), donc
						    on hydrate à l'identique ; persistStore (configureStore) réhydrate après le
						    montage via un re-render normal. */}
						<ResponsiveStateProvider>
							<LoadingBarProvider>
								<IntlProvider {...intl}>
									<AnalyticsContext.Provider value={{ store: store }}>
										<RumContextProvider componentName="AppComponent">
											<Router
												basename={`${
													envVars.PROXY_BASENAME_PREFIX || ""
												}/${shop}`}
											>
												<Routes>
													<Route
														path="*"
														element={
															<AppWithErrorBoundary
																initialFlagsData={initialFlagsData}
															/>
														}
													/>
												</Routes>
											</Router>
										</RumContextProvider>
									</AnalyticsContext.Provider>
								</IntlProvider>
							</LoadingBarProvider>
						</ResponsiveStateProvider>
					</Provider>
				</QueryClientProvider>
			</CacheProvider>
		</HelmetProvider>
	);

	loadableReady(() => {
		const container = document.querySelector("#perfectstay");
		// hydrateRoot ne doit être appelé qu'UNE fois par container (contrairement à
		// ReactDOM.hydrate en React 17). Le HMR (`module.hot.accept`) réexécute ce module,
		// donc on persiste la root pour la réutiliser (root.render) au lieu de re-hydrater,
		// ce qui montait une 2e copie de l'app par-dessus la première.
		if (window.__PFS_REACT_ROOT__) {
			window.__PFS_REACT_ROOT__.render(app);
		} else {
			window.__PFS_REACT_ROOT__ = hydrateRoot(container, app);
		}
		// timeout pour s'assurer que les photos chargent bien après le chat et le script Google Analytics
		setTimeout(injectAnalyticScripts, 500);
	});
};

if (module.hot) {
	module.hot.accept();
}
